<script setup lang="ts">
import type { ResolvedRange } from '@/lib/charts/timeRange'
import type { CustomerUsageSeries } from '@/types'
import ChartPanel from '@/components/charts/ChartPanel.vue'
import TimeSeriesChart from '@/components/charts/TimeSeriesChart.vue'
import { useCustomerUsageChartSeries } from '@/composables/useCustomerUsageChartSeries'
import { formatCount } from '@/lib/numbers'

const props = defineProps<{
  series: CustomerUsageSeries
  resolved: ResolvedRange
  loading?: boolean
}>()

const { customerSeries, chartSeries, hasData, total } = useCustomerUsageChartSeries(() => props.series, 'Total tokens')
</script>

<template>
  <ChartPanel
    title="Token usage over time"
    subtitle="Billable usage events for this product, broken down by customer."
    :loading="loading"
    :empty="!hasData"
    empty-text="No usage events in this range."
  >
    <template #latest>
      <span class="text-muted text-xs">
        Total over range: <span class="text-highlighted font-semibold tabular-nums">{{ formatCount(total) }}</span> events
      </span>
    </template>

    <TimeSeriesChart
      :series="chartSeries"
      :resolved="resolved"
      :height="280"
      :value-format="(value) => formatCount(value)"
    />

    <ul class="mt-4 flex flex-wrap gap-x-4 gap-y-2">
      <li v-for="entry in customerSeries" :key="entry.id" class="flex items-center gap-2 text-sm">
        <span class="h-0.5 w-3.5 rounded-full" :style="{ backgroundColor: entry.color }" />
        <span class="text-toned">{{ entry.label }}</span>
      </li>
    </ul>
  </ChartPanel>
</template>
